import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../styles/contact-dialog.css';
import { trackEvent } from '../utils/analytics';

interface ContactDialogProps {
  isVisible: boolean;
  onClose: () => void;
}

const ContactDialog: React.FC<ContactDialogProps> = ({ isVisible, onClose }) => {
  // Scrollen verhindern wenn Dialog offen ist
  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    // Cleanup beim unmount
    return () => {
      document.body.style.overflow = '';
    };
  }, [isVisible]);

  // ESC-Taste Listener
  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isVisible) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isVisible, onClose]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleLiveChatClick = (e: React.MouseEvent) => {
    e.preventDefault();
    trackEvent('Contact_LiveChat_Click');
    onClose();
    // Chatwoot öffnen, falls vorhanden
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((window as any).$chatwoot) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).$chatwoot.toggle();
    }
  };

  return (
    <>
      {/* Overlay */}
      <div 
        id="contact-overlay" 
        className={`contact-overlay ${isVisible ? 'active' : ''}`}
        onClick={handleOverlayClick}
        style={{
          display: isVisible ? 'block' : 'none'
        }}
      ></div>

      {/* Contact Dialog */}
      <div 
        id="contact-dialog" 
        className={`contact-dialog ${isVisible ? 'active' : ''}`}
        style={{
          display: isVisible ? 'block' : 'none'
        }}
      >
        <div className="contact-dialog-content">
          <div className="contact-dialog-header">
            <h3>Wie können wir dir helfen?</h3>
            <button 
              id="contact-close-button" 
              className="contact-close-button"
              onClick={onClose}
              aria-label="Dialog schließen"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>

          <p className="contact-dialog-description">
            Wähle deine bevorzugte Kontaktmethode aus und lass uns gemeinsam
            deine digitale Vision verwirklichen. Unser Team freut sich darauf,
            von dir zu hören!
          </p>

          <div className="contact-options">
            {/* WhatsApp Option */}
            <a
              href="https://wa.me/493042141523"
              className="contact-option"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent('Contact_WhatsApp_Click')}
            >
              <i className="fab fa-whatsapp contact-option-icon"></i>
              <h4 className="contact-option-title">WhatsApp</h4>
              <p className="contact-option-description">
                Sofortige Antworten und einfache Kommunikation per Chat
              </p>
            </a>

            {/* Beratungsgespräch Option */}
            <Link
              to="/beratungstermin"
              className="contact-option"
              onClick={() => {
                onClose();
                trackEvent('Contact_Beratungstermin_Click');
              }}
            >
              <i className="fas fa-calendar-check contact-option-icon"></i>
              <h4 className="contact-option-title">Beratungsgespräch</h4>
              <p className="contact-option-description">
                Buche direkt einen Termin für ein persönliches Gespräch
              </p>
            </Link>

            {/* E-Mail Option */}
            <a 
              href="mailto:info@stimm.digital" 
              className="contact-option"
              onClick={() => trackEvent('Contact_Email_Click')}
            >
              <i className="fas fa-envelope contact-option-icon"></i>
              <h4 className="contact-option-title">E-Mail</h4>
              <p className="contact-option-description">
                Für ausführliche Anfragen und Projektbeschreibungen
              </p>
            </a>

            {/* Live Chat Option */}
            <a 
              href="#" 
              className="contact-option" 
              id="contact-livechat-option"
              onClick={handleLiveChatClick}
            >
              <span className="contact-livechat-badge">Online</span>
              <i className="fas fa-comments contact-option-icon"></i>
              <h4 className="contact-option-title">Live Chat</h4>
              <p className="contact-option-description">
                Direkte Unterstützung von unserem Expertenteam
              </p>
            </a>
          </div>


        </div>
      </div>
    </>
  );
};

export default ContactDialog;
